import React, { useState, useEffect, useRef } from 'react';
import NotificationPanel from './NotificationPanel';
import { useNotificationPoll } from './useNotificationPoll';
import { useUnreadCount } from './useUnreadCount';
import { useTodos } from './todos';
import './notifications.css';

// Below this width the app navigates through the hamburger drawer, which
// carries its own Notifications entry and unread dot. The bell would be a
// duplicate touch target in an already cramped header, so it steps aside.
const MOBILE_NAV_MAX_WIDTH = 1099;

// The same test the CSS makes (and that shows the drawer), listened to as a
// media query: it changes when the layout does, even on an iPad rotation
const QUERY = `(max-width: ${MOBILE_NAV_MAX_WIDTH}px)`;
const useMobileNav = () => {
  const [on, setOn] = useState(() => (typeof window !== 'undefined' && window.matchMedia ? window.matchMedia(QUERY).matches : false));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia(QUERY);
    const change = () => setOn(mq.matches);
    change();
    if (mq.addEventListener) mq.addEventListener('change', change); else mq.addListener(change);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', change); else mq.removeListener(change); };
  }, []);
  return on;
};

const BellIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
  </svg>
);

/**
 * NotificationBell — inline top-bar bell button with a live unread badge.
 * Designed to be dropped into any top-bar container (CatalogueTopNav,
 * TopHeader, or a page-specific header). Sits in normal flex flow next to
 * the page's other action buttons; positioning is the parent's job.
 */
// The badge's pop-in animation should play once per session, when the count
// first arrives. Section top navs (settings, catalogue, ...) remount this bell
// on every tab click, and replaying the entrance each time read as a reload.
let badgeShownOnce = false;

// showOnMobile: the diary's phone header shows the bell too (it replaced the
// setup and "post ready" chips there), even though the menu carries one.
const NotificationBell = ({ className = '', showOnMobile = false }) => {
  const [open, setOpen] = useState(false);
  const settledRef = useRef(badgeShownOnce);
  // Bell badge is now driven by the server-tracked unread count
  // (refreshed by the cheap /unread-count poll), not by deriving from
  // the client-side items array. The full list is only fetched when
  // the panel opens — see NotificationPanel.
  const unreadCount = useUnreadCount();
  const isMobileNav = useMobileNav();
  const hidden = isMobileNav && !showOnMobile;
  // The number counts what needs you (To do); new updates alone show a dot.
  const todos = useTodos(!hidden);
  const needs = todos.count;
  const prevUnreadRef = useRef(unreadCount);
  const [ringing, setRinging] = useState(false);
  const [badgePopping, setBadgePopping] = useState(false);

  // SideNav owns the poll on mobile, where this bell does not render
  useNotificationPoll(!open && !isMobileNav);

  const signal = needs + unreadCount;
  useEffect(() => {
    if (signal > prevUnreadRef.current) {
      setRinging(true);
      setBadgePopping(true);
      const t1 = setTimeout(() => setRinging(false), 620);
      const t2 = setTimeout(() => setBadgePopping(false), 320);
      prevUnreadRef.current = signal;
      return () => { clearTimeout(t1); clearTimeout(t2); };
    }
    prevUnreadRef.current = signal;
    return undefined;
  }, [signal]);

  const displayCount = needs > 9 ? '9+' : String(needs);
  const setup = todos.setup;
  const ringC = 2 * Math.PI * 20;
  const ringP = setup && setup.total ? Math.min(1, setup.done / setup.total) : 0;

  // Mobile: the side menu carries notifications instead (all call sites keep
  // rendering this component; it simply stands down at that width)
  if (hidden) return null;

  return (
    <>
      <button
        type="button"
        className={`notif-bell-btn ${className} ${open ? 'notif-bell-btn--active' : ''}`}
        onClick={() => setOpen(prev => !prev)}
        aria-label={needs > 0 ? `Notifications, ${needs} to do` : unreadCount > 0 ? `Notifications, ${unreadCount} new` : 'Notifications'}
      >
        {setup && (
          <svg className="nt-bell-ring" viewBox="0 0 46 46" aria-hidden="true">
            <circle cx="23" cy="23" r="20" className="nt-ring-track" />
            <circle cx="23" cy="23" r="20" className="nt-ring-fill" strokeDasharray={`${(ringP * ringC).toFixed(1)} ${ringC.toFixed(1)}`} transform="rotate(-90 23 23)" />
          </svg>
        )}
        <span className={`notif-bell-icon ${ringing ? 'notif-bell-icon--ring' : ''}`}>
          <BellIcon />
          {needs > 0 && (badgeShownOnce = true) && (
            <span className={`notif-badge ${badgePopping ? 'notif-badge--pop' : ''} ${settledRef.current ? 'notif-badge--settled' : ''}`}>
              {displayCount}
            </span>
          )}
          {needs === 0 && unreadCount > 0 && <span className="notif-badge is-dot notif-badge--settled" aria-hidden="true" />}
        </span>
      </button>
      {open && <NotificationPanel onClose={() => setOpen(false)} />}
    </>
  );
};

// TODO(2026-05-27): Notifications are still being tested end-to-end.
// Hide the bell across the app until that lands by gating the default
// export. Flip NOTIFICATIONS_BELL_ENABLED back to true to re-enable
// everywhere — all 10+ call sites stay untouched. Side-effects (the
// poll, the panel) only fire from the real component; the hidden
// stub renders nothing and runs no hooks.
const NOTIFICATIONS_BELL_ENABLED = true;

const HiddenBell = () => null;

export default NOTIFICATIONS_BELL_ENABLED ? NotificationBell : HiddenBell;
